/**
 * Feuille du sélecteur de contexte — téléphone (U6b vague 9, § 4.8, planche SelecteurTelephone).
 *
 * Au téléphone, la barre de contexte (organisation › perspective › objet) ne tient pas sous la
 * barre haute : elle passe dans une FEUILLE plein écran ouverte par la pastille du contexte.
 * La barre reste MONTÉE en permanence (masquée quand la feuille est fermée) : ses effets —
 * validation d'un lien profond, mémoire du dernier contexte, accès retiré — tournent donc au
 * téléphone comme au bureau, une seule fois (jamais deux montages concurrents).
 *
 * Contrat du dialogue : `role="dialog"`, `aria-modal`, nommé par son titre ; le focus entre sur
 * « Fermer sans changer de contexte », Tab reste confiné, Échap ferme SANS changement, et le
 * focus revient à l'élément qui a ouvert la feuille (la pastille). Tout changement de route
 * (un contexte choisi) ferme la feuille.
 */
import { useEffect, useId, useRef, type JSX, type KeyboardEvent, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { X } from 'lucide-react'

import { useT } from '@/i18n/language'

const FOCALISABLES = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function FeuilleContexte({ telephone, ouvert, onFermer, children }: {
  telephone: boolean
  ouvert: boolean
  onFermer: () => void
  children: ReactNode
}): JSX.Element {
  const { t } = useT()
  const { pathname, search } = useLocation()
  const idTitre = useId()
  const racine = useRef<HTMLDivElement>(null)
  const fermer = useRef<HTMLButtonElement>(null)
  const retour = useRef<HTMLElement | null>(null)
  const actif = telephone && ouvert

  // Ouverture : mémoriser l'élément d'origine, entrer dans la feuille. Fermeture : y revenir.
  useEffect(() => {
    if (!actif) return
    retour.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    fermer.current?.focus()
    return () => {
      const origine = retour.current
      if (origine && document.contains(origine)) origine.focus()
    }
  }, [actif])

  // Un contexte choisi change la route : la feuille se ferme.
  const route = `${pathname}${search}`
  const routeVue = useRef(route)
  useEffect(() => {
    if (routeVue.current === route) return
    routeVue.current = route
    if (ouvert) onFermer()
  }, [route, ouvert, onFermer])

  if (!telephone) return <>{children}</>

  const clavier = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Escape') {
      // Un panneau interne (liste du sélecteur) consomme Échap d'abord ; sinon on ferme.
      if (e.defaultPrevented) return
      e.preventDefault()
      onFermer()
      return
    }
    if (e.key !== 'Tab' || !racine.current) return
    const cibles = Array.from(racine.current.querySelectorAll<HTMLElement>(FOCALISABLES))
      .filter((el) => el.closest('[hidden]') === null)
    if (cibles.length === 0) return
    const premier = cibles[0]!
    const dernier = cibles[cibles.length - 1]!
    if (e.shiftKey && document.activeElement === premier) { e.preventDefault(); dernier.focus() }
    else if (!e.shiftKey && document.activeElement === dernier) { e.preventDefault(); premier.focus() }
  }

  return (
    <div ref={racine} data-feuille-contexte="" data-testid="feuille-contexte" hidden={!ouvert}
         {...(ouvert ? { role: 'dialog', 'aria-modal': true, 'aria-labelledby': idTitre } : {})}
         onKeyDown={ouvert ? clavier : undefined}
         className={ouvert ? 'fixed inset-0 z-50 flex flex-col overflow-y-auto bg-background' : undefined}>
      {ouvert ? (
        <div className="sticky top-0 z-[1] flex items-center gap-2 border-b bg-background px-2 py-1">
          <button ref={fermer} type="button" onClick={onFermer} data-testid="feuille-contexte-fermer"
                  aria-label={t('contexte.fermerSansChanger', 'Fermer sans changer de contexte')}
                  className="flex size-11 shrink-0 items-center justify-center rounded-md hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring">
            <X className="size-5" aria-hidden="true" />
          </button>
          <h2 id={idTitre} className="text-base font-semibold">
            {t('contexte.feuilleTitre', 'Changer de programme ou de projet')}
          </h2>
        </div>
      ) : null}
      {children}
    </div>
  )
}
